import apiClient from './apiClient'
import type {
  ApiCustomView,
  ApiCustomViewNote,
  ApiCustomViewShare,
  CopyAllNotesToCustomViewResponse,
  ContentType,
  CopyCustomViewRequest,
  CopyCustomViewResponse,
  CopyNoteToCustomViewRequest,
  CreateCustomViewShareRequest,
  CreateCustomViewShareResponse,
  CustomViewMutationRequest,
  GetCustomViewNoteHistoryResponse,
  GetCustomViewNotesResponse,
  GetCustomViewSharesResponse,
  GetCustomViewsResponse,
  UpdateCustomViewShareRequest,
  UpsertCustomViewNoteRequest,
} from '@/types/api'

/**
 * GET /api/custom-views
 * Get all custom views for the current user.
 */
export async function fetchCustomViews(): Promise<GetCustomViewsResponse> {
  const { data } = await apiClient.get<GetCustomViewsResponse>('/custom-views')
  return data
}

/**
 * POST /api/custom-views
 * Create a new custom view.
 */
export async function createCustomView(request: CustomViewMutationRequest): Promise<ApiCustomView> {
  const { data } = await apiClient.post<ApiCustomView>('/custom-views', request)
  return data
}

/**
 * PUT /api/custom-views/{id}
 * Update an existing custom view.
 */
export async function updateCustomView(
  id: number,
  request: CustomViewMutationRequest
): Promise<ApiCustomView> {
  const { data } = await apiClient.put<ApiCustomView>(`/custom-views/${id}`, request)
  return data
}

/**
 * DELETE /api/custom-views/{id}
 * Delete a custom view. Returns 204 No Content.
 */
export async function deleteCustomView(id: number): Promise<void> {
  await apiClient.delete(`/custom-views/${id}`)
}

/**
 * GET /api/custom-views/{id}/shares
 * Owner only.
 */
export async function fetchCustomViewShares(viewId: number): Promise<GetCustomViewSharesResponse> {
  const { data } = await apiClient.get<GetCustomViewSharesResponse>(`/custom-views/${viewId}/shares`)
  return data
}

/**
 * POST /api/custom-views/{id}/shares
 * Cross-domain shares wait for the recipient to accept and stay out of the owner's share list until then.
 */
export async function createCustomViewShare(
  viewId: number,
  request: CreateCustomViewShareRequest
): Promise<CreateCustomViewShareResponse> {
  const { data } = await apiClient.post<CreateCustomViewShareResponse>(`/custom-views/${viewId}/shares`, request)
  return data
}

/**
 * PUT /api/custom-views/{id}/shares/{shareId}
 */
export async function updateCustomViewShare(
  viewId: number,
  shareId: number,
  request: UpdateCustomViewShareRequest
): Promise<ApiCustomViewShare> {
  const { data } = await apiClient.put<ApiCustomViewShare>(`/custom-views/${viewId}/shares/${shareId}`, request)
  return data
}

/**
 * DELETE /api/custom-views/{id}/shares/{shareId}
 * Owner revokes a share.
 */
export async function deleteCustomViewShare(viewId: number, shareId: number): Promise<void> {
  await apiClient.delete(`/custom-views/${viewId}/shares/${shareId}`)
}

/**
 * POST /api/custom-views/{id}/accept
 * Recipient accepts a cross-domain share. Declining is leaveCustomViewShare.
 */
export async function acceptCustomView(viewId: number): Promise<ApiCustomView> {
  const { data } = await apiClient.post<ApiCustomView>(`/custom-views/${viewId}/accept`, {})
  return data
}

/**
 * DELETE /api/custom-views/{id}/shares/me
 * Recipient removes a shared view from their own list.
 */
export async function leaveCustomViewShare(viewId: number): Promise<void> {
  await apiClient.delete(`/custom-views/${viewId}/shares/me`)
}

/**
 * POST /api/custom-views/{id}/copies
 * Clones the view into a new view owned by the target user.
 */
export async function copyCustomView(viewId: number, request: CopyCustomViewRequest): Promise<CopyCustomViewResponse> {
  const { data } = await apiClient.post<CopyCustomViewResponse>(`/custom-views/${viewId}/copies`, request)
  return data
}

/**
 * GET /api/custom-views/{id}/notes
 * Notes owned by the view itself, readable by everyone it is shared with.
 */
export async function fetchCustomViewNotes(viewId: number): Promise<GetCustomViewNotesResponse> {
  const { data } = await apiClient.get<GetCustomViewNotesResponse>(`/custom-views/${viewId}/notes`)
  return data
}

/**
 * PUT /api/custom-views/{id}/notes
 */
export async function upsertCustomViewNote(
  viewId: number,
  request: UpsertCustomViewNoteRequest
): Promise<ApiCustomViewNote> {
  const { data } = await apiClient.put<ApiCustomViewNote>(`/custom-views/${viewId}/notes`, request)
  return data
}

/**
 * DELETE /api/custom-views/{id}/notes/{contentType}/{entityId}
 */
export async function deleteCustomViewNote(
  viewId: number,
  contentType: ContentType,
  entityId: number,
  expectedRowVersion?: string | null
): Promise<void> {
  // DELETE has no body, so the concurrency token rides along as a query param.
  await apiClient.delete(`/custom-views/${viewId}/notes/${contentType}/${entityId}`, {
    params: expectedRowVersion ? { expectedRowVersion } : undefined,
  })
}

/**
 * GET /api/custom-views/{id}/notes/{contentType}/{entityId}/history
 * Prior revisions from the system-versioned history table, newest first.
 */
export async function fetchCustomViewNoteHistory(
  viewId: number,
  contentType: ContentType,
  entityId: number
): Promise<GetCustomViewNoteHistoryResponse> {
  const { data } = await apiClient.get<GetCustomViewNoteHistoryResponse>(
    `/custom-views/${viewId}/notes/${contentType}/${entityId}/history`
  )
  return data
}

/**
 * POST /api/custom-views/{id}/notes/copy
 * Owner only: replaces the view note for one entity with the owner's personal note.
 */
export async function copyNoteToCustomView(
  viewId: number,
  request: CopyNoteToCustomViewRequest
): Promise<ApiCustomViewNote> {
  const { data } = await apiClient.post<ApiCustomViewNote>(`/custom-views/${viewId}/notes/copy`, request)
  return data
}

/**
 * POST /api/custom-views/{id}/notes/copy-all
 * Owner only, and only while the view has no notes of its own.
 */
export async function copyAllNotesToCustomView(viewId: number): Promise<CopyAllNotesToCustomViewResponse> {
  const { data } = await apiClient.post<CopyAllNotesToCustomViewResponse>(`/custom-views/${viewId}/notes/copy-all`, {})
  return data
}
